import { groupBy } from 'lodash';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { Role } from 'types';
import { useConfirmApi } from 'utils/api/ApiHooks';
import { useIsSuperAdmin } from 'utils/models/User';
import { useFeatures } from 'utils/util/features';
import SimpleErrorPage from 'views/Error/SimpleErrorPage';
import UserAdministration from 'views/Organization/UserAdministration';
import Loading from 'views/Widgets/Loading';
import AdminRolesSection from './PeopleAdministration/Administrators/AdminRolesSection';
import { PersonRole } from './PeopleAdministration/Administrators/types';

type PersonRoleListApiResponse = {
  results: PersonRole[];
};

const NO_ADMINS = [];
const PeopleAdministrationAdministrators: FC = () => {
  const { formatMessage } = useIntl();
  const [admins, setAdmins] = useState<PersonRole[]>([]);
  const { isEnabled, ifEnabled, isGroupAndOptionEnabled } = useFeatures();
  const isSuperAdmin = useIsSuperAdmin();

  const onSuccesfulRetrieve = useCallback(
    (
      data: PersonRoleListApiResponse | null,
      error: unknown,
      hardErrorMessage: unknown
    ): void => {
      if (!error && !hardErrorMessage) {
        setAdmins(data?.results ?? NO_ADMINS);
      }
    },
    []
  );

  const { data, error, status } = useConfirmApi<PersonRoleListApiResponse>({
    url: `/person-roles`,
    method: 'GET',
    callback: onSuccesfulRetrieve,
  });

  const notifyAdminListChanged = useCallback(
    (item: PersonRole) => {
      if (item.role === Role.HR_ADMIN) {
        toast.warning(
          formatMessage({
            id: 'app.views.administration.people_administration.message.administrator_list_updated',
            defaultMessage:
              'HR Administrators updated. Changes will propagate through the system and take effect in the next minutes.',
          })
        );
      } else {
        toast.success(
          formatMessage({
            id: 'app.views.administration.people_administration.message.update_immediately',
            defaultMessage:
              'Configuration applied successfully. Changes will take effect immediately.',
          })
        );
      }
    },
    [formatMessage]
  );

  const handleItemChanged = useCallback(
    (item: PersonRole) => {
      setAdmins((prev) => {
        const index = prev.findIndex((i) => i.id === item.id);
        if (index === -1) {
          return [...prev, item];
        }
        return [...prev.slice(0, index), item, ...prev.slice(index + 1)];
      });
      notifyAdminListChanged(item);
    },
    [notifyAdminListChanged]
  );

  const handleItemDeleted = useCallback(
    (item: PersonRole) => {
      setAdmins((prev) => prev.filter((i) => i.id !== item.id));
      notifyAdminListChanged(item);
    },
    [notifyAdminListChanged]
  );

  const {
    SYSTEM_ADMIN = NO_ADMINS,
    HR_ADMIN = NO_ADMINS,
    ENGAGEMENT_SURVEY_ADMIN = NO_ADMINS,
    RATING_DASHBOARD_ADMIN = NO_ADMINS,
    DEVELOPMENT_PLAN_ADMIN = NO_ADMINS,
    ONE_ON_ONE_ADMIN = NO_ADMINS,
    AI_ADMIN = NO_ADMINS,
    OBJECTIVES_ADMIN = NO_ADMINS,
    PROMOTION_PACKET_ADMIN = NO_ADMINS,
    CALIBRATION_VIEW_EDITOR = NO_ADMINS,
  } = useMemo(() => groupBy(admins ?? [], 'role'), [admins]);

  if (status === 'LOADING') {
    return (
      <Loading
        message={formatMessage({
          id: 'app.views.administration.people_administration_administrators_beta.message.loading_data_about_administrators',
          defaultMessage: 'Loading data about administrators...',
        })}
      />
    );
  }

  if (!data || error) {
    return <SimpleErrorPage error={error} />;
  }

  const isBetaFeatureEnabled = isEnabled('people_admin_admins_beta');

  return (
    <>
      {isBetaFeatureEnabled && (
        <>
          <AdminRolesSection
            role={Role.SYSTEM_ADMIN}
            title={formatMessage({
              id: 'app.user.organization.user_administration.cards.title.system_administrators',
              defaultMessage: 'System administrators',
            })}
            description={formatMessage({
              id: 'app.user.organization.user_administration.cards.description.system_administrators',
              defaultMessage:
                'Typically technical admins. They have full system access to configure all aspects of Confirm for your organization.',
            })}
            emptyStateTitle={formatMessage({
              id: 'app.user.organization.user_administration.cards.empty_state_title.system_administrators',
              defaultMessage:
                'There are no system administrators assigned to this organization.',
            })}
            values={SYSTEM_ADMIN}
            onItemChange={handleItemChanged}
            onItemDelete={handleItemDeleted}
          />

          <AdminRolesSection
            role={Role.HR_ADMIN}
            title={formatMessage({
              id: 'app.user.organization.user_administration.cards.title.hr_administrators',
              defaultMessage: 'HR administrators',
            })}
            description={formatMessage({
              id: 'app.user.organization.user_administration.cards.description.hr_administrators',
              defaultMessage:
                'Typically HRBPs i.e. HR/People admins. They have HR admin access to specific groups of people across all campaigns.',
            })}
            emptyStateTitle={formatMessage({
              id: 'app.user.organization.user_administration.cards.empty_state_title.hr_administrators',
              defaultMessage:
                'There are no HR administrators assigned to this organization.',
            })}
            values={HR_ADMIN}
            onItemChange={handleItemChanged}
            onItemDelete={handleItemDeleted}
          />

          <AdminRolesSection
            role={Role.ENGAGEMENT_SURVEY_ADMIN}
            title={formatMessage({
              id: 'app.user.organization.user_administration.cards.title.es_administrators',
              defaultMessage: 'Engagement dashboard administrators',
            })}
            description={formatMessage({
              id: 'app.user.organization.user_administration.cards.description.es_administrators',
              defaultMessage:
                'Typically executives (and some HRBPs i.e. HR/People admins). They can see the results of engagement surveys, along with HR administrators. ',
            })}
            emptyStateTitle={formatMessage({
              id: 'app.user.organization.user_administration.cards.empty_state_title.es_administrators',
              defaultMessage:
                'There are no engagement dashboard administrators assigned to this organization.',
            })}
            values={ENGAGEMENT_SURVEY_ADMIN}
            onItemChange={handleItemChanged}
            onItemDelete={handleItemDeleted}
          />

          <AdminRolesSection
            role={Role.RATING_DASHBOARD_ADMIN}
            title={formatMessage({
              id: 'app.user.organization.user_administration.cards.title.rd_administrators',
              defaultMessage: 'Rating dashboard administrators',
            })}
            description={formatMessage({
              id: 'app.user.organization.user_administration.cards.description.rd_administrators',
              defaultMessage:
                'Typically executives (and some HRBPs i.e. HR/People admins). They can see the rating dashboard of all Performance ratings.',
            })}
            emptyStateTitle={formatMessage({
              id: 'app.user.organization.user_administration.cards.empty_state_title.rd_administrators',
              defaultMessage:
                'There are no rating dashboard administrators assigned to this organization.',
            })}
            values={RATING_DASHBOARD_ADMIN}
            onItemChange={handleItemChanged}
            onItemDelete={handleItemDeleted}
          />

          {ifEnabled(
            'development_plans',
            <AdminRolesSection
              role={Role.DEVELOPMENT_PLAN_ADMIN}
              title={formatMessage({
                id: 'app.user.organization.user_administration.cards.title.dp_administrators',
                defaultMessage: 'Development plan administrators',
              })}
              description={formatMessage({
                id: 'app.user.organization.user_administration.cards.description.dp_administrators',
                defaultMessage:
                  'Typically executives (and some HRBPs i.e. HR/People admins). They can see specifically designed development plans of all employees.',
              })}
              emptyStateTitle={formatMessage({
                id: 'app.user.organization.user_administration.cards.empty_state_title.dp_administrators',
                defaultMessage:
                  'There are no develpment plan administrators assigned to this organization.',
              })}
              values={DEVELOPMENT_PLAN_ADMIN}
              onItemChange={handleItemChanged}
              onItemDelete={handleItemDeleted}
            />,
            null
          )}
          {isEnabled('one_on_ones') &&
            isEnabled('one_on_ones_administration') &&
            isEnabled('content_administration') && (
              <AdminRolesSection
                role={Role.ONE_ON_ONE_ADMIN}
                title={formatMessage({
                  id: 'app.user.organization.user_administration.cards.title.one_on_one_administrators',
                  defaultMessage: 'One-on-one administrators',
                })}
                description={formatMessage({
                  id: 'app.user.organization.user_administration.cards.description.one_on_one_administrators',
                  defaultMessage:
                    'Typically HRBPs i.e. HR/People admins. They have access to administer 1:1s for specific groups of people.',
                })}
                emptyStateTitle={formatMessage({
                  id: 'app.user.organization.user_administration.cards.empty_state_title.one_on_one_administrators',
                  defaultMessage:
                    'There are no One-on-one administrators assigned to this organization.',
                })}
                values={ONE_ON_ONE_ADMIN}
                onItemChange={handleItemChanged}
                onItemDelete={handleItemDeleted}
              />
            )}
          {(isGroupAndOptionEnabled('ai_assistant', 'ai_agents') ||
            isSuperAdmin) && (
            <AdminRolesSection
              role={Role.AI_ADMIN}
              title={formatMessage({
                id: 'app.user.organization.user_administration.cards.title.ai_administrators',
                defaultMessage: 'AI administrators',
              })}
              description={formatMessage({
                id: 'app.user.organization.user_administration.cards.description.ai_administrators',
                defaultMessage:
                  'They have access to manage AI agents and AI-related configurations for the organization.',
              })}
              emptyStateTitle={formatMessage({
                id: 'app.user.organization.user_administration.cards.empty_state_title.ai_administrators',
                defaultMessage:
                  'There are no AI administrators assigned to this organization.',
              })}
              values={AI_ADMIN}
              onItemChange={handleItemChanged}
              onItemDelete={handleItemDeleted}
            />
          )}
          {isGroupAndOptionEnabled('objectives', 'admins_enabled') && (
            <AdminRolesSection
              role={Role.OBJECTIVES_ADMIN}
              title={formatMessage({
                id: 'app.user.organization.user_administration.cards.title.objectives_administrators',
                defaultMessage: 'Objectives administrators',
              })}
              description={formatMessage({
                id: 'app.user.organization.user_administration.cards.description.objectives_administrators',
                defaultMessage:
                  'They have access to manage objectives and OKRs configurations for the organization.',
              })}
              emptyStateTitle={formatMessage({
                id: 'app.user.organization.user_administration.cards.empty_state_title.objectives_administrators',
                defaultMessage:
                  'There are no Objectives administrators assigned to this organization.',
              })}
              values={OBJECTIVES_ADMIN}
              onItemChange={handleItemChanged}
              onItemDelete={handleItemDeleted}
            />
          )}
          {isEnabled('promotion_packets') &&
            isGroupAndOptionEnabled(
              'content_administration',
              'templates_enabled'
            ) && (
              <AdminRolesSection
                role={Role.PROMOTION_PACKET_ADMIN}
                title={formatMessage({
                  id: 'app.user.organization.user_administration.cards.title.promotion_packet_administrators',
                  defaultMessage: 'Promotion packet administrators',
                })}
                description={formatMessage({
                  id: 'app.user.organization.user_administration.cards.description.promotion_packet_administrators',
                  defaultMessage:
                    'They can manage the promotion packet questions, templates and defaults of the organization.',
                })}
                emptyStateTitle={formatMessage({
                  id: 'app.user.organization.user_administration.cards.empty_state_title.promotion_packet_administrators',
                  defaultMessage:
                    'There are no Promotion packet administrators assigned to this organization.',
                })}
                values={PROMOTION_PACKET_ADMIN}
                onItemChange={handleItemChanged}
                onItemDelete={handleItemDeleted}
              />
            )}
          {isGroupAndOptionEnabled('performance', 'calibration_dashboard') && (
            <AdminRolesSection
              role={Role.CALIBRATION_VIEW_EDITOR}
              title={formatMessage({
                id: 'app.user.organization.user_administration.cards.title.calibration_view_editors',
                defaultMessage: 'Calibration view editors',
              })}
              description={formatMessage({
                id: 'app.user.organization.user_administration.cards.description.calibration_view_editors',
                defaultMessage:
                  'They can create and manage calibration views, even if they are not an HR administrator, for people in the cycle(s) and target audience you list below.',
              })}
              emptyStateTitle={formatMessage({
                id: 'app.user.organization.user_administration.cards.empty_state_title.calibration_view_editors',
                defaultMessage:
                  'There are no calibration view editors assigned to this organization.',
              })}
              values={CALIBRATION_VIEW_EDITOR}
              onItemChange={handleItemChanged}
              onItemDelete={handleItemDeleted}
            />
          )}
        </>
      )}
      <UserAdministration showOnlyNonDefaults={isBetaFeatureEnabled} />
    </>
  );
};

export default React.memo(PeopleAdministrationAdministrators);
